import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import orderBy from 'lodash/orderBy';
import moment from 'moment';

import { useTranslation, Trans } from 'react-i18next';

// ui
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import TableContainer from '@material-ui/core/TableContainer';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Tooltip from '@material-ui/core/Tooltip';

// font and icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine } from '@fortawesome/free-solid-svg-icons';

// components
import { StyledIconButton } from '../../../../components/button/Button';
import TableHeaderEnhance from '../../../../components/table/TableHeaderEnhance';
import areas from '../../../../assets/json/region';
import DamLargeColor from '../../../../components/color/dam/DamLargeColor';
import DamLargeColorCurve from '../../../../components/color/dam/DamLargeColorCurve';
import DamChartDailyMainEgat from '../../chart/daily/DamChartDailyMainEgat';
import DamChartDailyByRegionMainEgat from '../../chart/daily/DamChartDailyByRegionMainEgat';

// utils
import formatNumber from '../../../../utils/number/Number';

// styles
import styles from '../style';
import {
  StyledCommentTableCell,
  StyledFooterSubTableCell,
  StyledFooterTableCell,
  StyledSubTableCell,
} from '../../../../components/table/StyleTable';
import '../../styles.css';

const columns = [
  {
    id: 'dam.dam_name.th',
    disablePadding: false,
    label: <Trans>อ่างเก็บน้ำ</Trans>,
    sortable: true,
  },
  {
    id: 'geocode.province_name.th',
    numeric: false,
    disablePadding: true,
    label: <Trans>จังหวัด</Trans>,
    sortable: true,
  },
  {
    id: 'basin.basin_name.th',
    label: <Trans>ลุ่มน้ำ</Trans>,
    sortable: true,
  },
  {
    id: 'dam_level',
    align: 'right',
    numeric: true,
    disablePadding: false,
    label: <Trans>ระดับน้ำ</Trans>,
    unit: <Trans>ม.รทก.</Trans>,
    sortable: true,
  },
  {
    id: 'normal_storage',
    align: 'right',
    numeric: true,
    disablePadding: false,
    label: <Trans>ความจุ</Trans>,
    unit: <Trans>ล้าน ลบ.ม.</Trans>,
    sortable: true,
  },
  {
    id: 'dam_storage',
    align: 'right',
    numeric: true,
    disablePadding: false,
    label: <Trans>ปริมาณน้ำกักเก็บ</Trans>,
    unit: <Trans>ล้าน ลบ.ม.</Trans>,
    sortable: true,
  },
  {
    id: 'percent_storage',
    align: 'right',
    numeric: true,
    disablePadding: false,
    label: <Trans>คิดเป็น % ของน้ำเต็มอ่าง</Trans>,
    unit: <Trans>%</Trans>,
    sortable: true,
  },
  {
    id: 'dam_inflow',
    align: 'right',
    numeric: true,
    disablePadding: false,
    label: <Trans>น้ำไหลลง</Trans>,
    unit: <Trans>ล้าน ลบ.ม.</Trans>,
    sortable: true,
  },
  {
    id: 'dam_released',
    align: 'right',
    numeric: true,
    disablePadding: false,
    label: <Trans>น้ำระบาย</Trans>,
    unit: <Trans>ล้าน ลบ.ม.</Trans>,
    sortable: true,
  },
  {
    id: 'chart',
    numeric: false,
    disablePadding: false,
    label: '',
  },
];

const useStyles = makeStyles(styles);

export default function DamLargeDataListsEgat(props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const { datas, handleModalChanage, useRuleCurve } = props;
  const filterData = datas.filter((d) => d.agency.id === 8);
  // const unmounted = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const currentDate = moment().format('D/M/2020');

  const setColor = (volume, storagePercent, damID) => {
    let status = null;
    if (useRuleCurve) {
      status = DamLargeColorCurve(volume, storagePercent, damID, currentDate, 'egat');
    } else {
      status = DamLargeColor(storagePercent);
    }

    return status;
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // table sorting
  const [state, setState] = useState({
    order: 'desc',
    orderBy: 'dam_storage_percent',
  });

  // set total summary
  let grandTotalInflow = 0;
  let grandTotalReleased = 0;
  let grandTotalDamStorage = 0;
  let grandTotalNormalStorage = 0;
  // let grandTotalMaxStorage = 0;

  // force modal close
  // useEffect(() => {
  //   if (modalData.open) modalData.open = false;
  //   return () => { unmounted.current = true; };
  // }, [modalData.open]);

  const handleChart = (data) => (e) => {
    e.preventDefault();

    handleModalChanage({
      open: true,
      header: t('กราฟอ่างเก็บน้ำรายวัน การไฟฟ้า'),
      subheader: null,
      content: <DamChartDailyMainEgat id={data} agencyId={12} />,
    });
  };

  const handleChartSumByRegion = (regionId) => (e) => {
    e.preventDefault();

    handleModalChanage({
      open: true,
      header: t('กราฟอ่างเก็บน้ำรายวัน รายภาค การไฟฟ้า'),
      subheader: null,
      content: <DamChartDailyByRegionMainEgat id={regionId} />,
    });
  };

  const handleRequestSort = (event, property) => {
    const orderByProperty = property;
    let orderLocal = 'desc';

    if (state.orderBy === property && state.order === 'desc') {
      orderLocal = 'asc';
    }

    setState({ order: orderLocal, orderBy: orderByProperty });
  };

  return (
    <>
      <Paper className={classes.root}>
        <TableContainer style={{ maxHeight: 650 }}>
          <Table stickyHeader className={classes.table} id="large_dam">
            {/* table header */}
            <TableHead>
              <TableRow>
                <StyledCommentTableCell colSpan={3} align="center">{t('พิ้นที่')}</StyledCommentTableCell>
                <StyledCommentTableCell colSpan={2} align="center">{t('ความจุ')}</StyledCommentTableCell>
                <StyledCommentTableCell colSpan={2} align="center">{t('น้ำในอ่าง')}</StyledCommentTableCell>
                <StyledCommentTableCell colSpan={1} align="center">{t('ปริมาณน้ำไหลลงอ่าง')}</StyledCommentTableCell>
                <StyledCommentTableCell colSpan={1} align="center">{t('ปริมาณน้ำระบาย')}</StyledCommentTableCell>
                <StyledCommentTableCell colSpan={1} align="center" />
              </TableRow>
            </TableHead>
            <TableHeaderEnhance
              firstColumnSticky
              columnData={columns}
              order={state.order}
              orderBy={state.orderBy}
              onRequestSort={handleRequestSort}
            />
            {/* END table header */}

            <TableBody>
              {filterData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={14}>
                    {isLoading ? ( // Check if data is loading
                      <strong>{t('กำลังโหลดข้อมูล...')}</strong>
                    ) : (
                      <strong>{t('ไม่มีข้อมูลแหล่งน้ำขนาดใหญ่ ตามเงื่อนไข')}</strong>
                    )}
                  </TableCell>
                </TableRow>
              )}

              {/* area group title */}
              {areas
                .map((area) => {
                  // summary value by region
                  let totalInflow = 0;
                  let totalReleased = 0;
                  let totalDamStorage = 0;
                  let totalMaxStorage = 0;
                  let percentStorage = 0;

                  // count total records for each area
                  let totalDamInArea = 0;
                  if (filterData.length > 0) {
                    totalDamInArea = filterData
                      .filter((row) => row.geocode.area_code === area.id.toString())
                      .length;
                  }
                  if (totalDamInArea === 0) return null;

                  return (
                    <Fragment key={area.id}>
                      <TableRow>
                        <StyledSubTableCell bgcolor="primary" className="first-column-sticky">
                          {t(area.name)}
                          {' '}
                          {`(${totalDamInArea})`}
                        </StyledSubTableCell>
                        <StyledSubTableCell colSpan={14} bgcolor="primary" />
                      </TableRow>

                      {/* data rows */}
                      {orderBy(filterData, state.orderBy, state.order)
                        .filter((row) => row.geocode.area_code === area.id.toString())
                        .map((row) => {
                          totalInflow += row.dam_inflow;
                          totalReleased += row.dam_released;

                          if (!Number.isNaN(parseFloat(row.dam.normal_storage))) {
                            totalMaxStorage += row.dam.normal_storage;
                          }
                          totalDamStorage += row.dam_storage;

                          grandTotalInflow += row.dam_inflow;
                          grandTotalReleased += row.dam_released;
                          grandTotalDamStorage += row.dam_storage;
                          // if (!Number.isNaN(parseFloat(row.dam.max_storage))) {
                          //   grandTotalMaxStorage += row.dam.max_storage;
                          // }
                          if (!Number.isNaN(parseFloat(row.dam.normal_storage))) {
                            grandTotalNormalStorage += row.dam.normal_storage;
                          }

                          percentStorage = ((100 * row.dam_storage) / row.dam.normal_storage)
                            .toFixed(0);

                          const status = setColor(row.dam_storage, percentStorage, row.dam.id);

                          return (
                            <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                              <TableCell className="first-column-sticky">
                                {row.dam.dam_name.th}
                              </TableCell>
                              <TableCell>
                                {row.geocode.province_name.th}
                              </TableCell>
                              <TableCell>
                                {row.basin.basin_name.th.replace('ลุ่มน้ำ', '')}
                              </TableCell>
                              <TableCell align="right">
                                {formatNumber(row.dam_level)}
                              </TableCell>
                              <TableCell align="right">
                                {formatNumber(row.dam.normal_storage)}
                              </TableCell>
                              <TableCell align="right">
                                <span style={useRuleCurve ? { color: status.color.statusColor } : {}}>
                                  {formatNumber(row.dam_storage)}
                                </span>
                              </TableCell>
                              <TableCell align="right">
                                <span style={useRuleCurve ? { } : { color: status.color.statusColor }}>
                                  <strong>{!Number.isNaN(parseFloat(percentStorage), 10) ? formatNumber(parseFloat(percentStorage).toFixed(0)) : '-'}</strong>
                                </span>
                              </TableCell>
                              <TableCell align="right">
                                {row.dam_inflow.toFixed(2)}
                              </TableCell>
                              <TableCell align="right">
                                {row.dam_released.toFixed(2)}
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title={t('กราฟรายปี')}>
                                  <StyledIconButton onClick={handleChart(row.dam.id)}>
                                    <FontAwesomeIcon icon={faChartLine} style={{ color: '#888' }} />
                                  </StyledIconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      {/* END data rows */}

                      {/* sum by each region */}
                      <TableRow>
                        <StyledFooterTableCell className="first-column-sticky">
                          <Box fontWeight="bold">
                            {t(`รวม${area.name}`)}
                          </Box>
                        </StyledFooterTableCell>
                        <StyledFooterTableCell />
                        <StyledFooterTableCell />
                        <StyledFooterTableCell />
                        <StyledFooterTableCell align="right">
                          <strong>
                            {!Number.isNaN(totalMaxStorage, 10) ? formatNumber(totalMaxStorage.toFixed(0)) : '-'}
                          </strong>
                        </StyledFooterTableCell>
                        <StyledFooterTableCell align="right">
                          <strong>
                            {!Number.isNaN(totalDamStorage, 10) ? formatNumber(totalDamStorage.toFixed(0)) : '-'}
                          </strong>
                        </StyledFooterTableCell>
                        <StyledFooterTableCell />
                        <StyledFooterTableCell align="right">
                          <strong>
                            {totalInflow.toFixed(2)}
                          </strong>
                        </StyledFooterTableCell>
                        <StyledFooterTableCell align="right">
                          <strong>
                            {totalReleased.toFixed(2)}
                          </strong>
                        </StyledFooterTableCell>
                        <StyledFooterTableCell align="right">
                          <Tooltip title={t('กราฟรายปี')}>
                            <StyledIconButton onClick={handleChartSumByRegion(area.id)}>
                              <FontAwesomeIcon icon={faChartLine} style={{ color: '#888' }} />
                            </StyledIconButton>
                          </Tooltip>
                          {/* <Tooltip title="กราฟเปรียบเทียบรายวัน">
                            <StyledIconButton onClick={handleChartCompareDailySumByRegion(area.id)}>
                              <FontAwesomeIcon icon={faChartBar} style={{ color: '#888' }} />
                            </StyledIconButton>
                          </Tooltip> */}
                        </StyledFooterTableCell>
                      </TableRow>
                      {/* END sum by each region */}
                    </Fragment>
                  );
                })}
              {/* END area group title */}

              {/* grand total all regions */}
              {filterData.length !== 0 && (
                <TableRow>
                  <StyledFooterSubTableCell className="first-column-sticky">
                    <Box fontWeight="bold">{t('รวมทั้งประเทศ')}</Box>
                  </StyledFooterSubTableCell>
                  <StyledFooterSubTableCell />
                  <StyledFooterSubTableCell />
                  <StyledFooterSubTableCell />
                  <StyledFooterSubTableCell align="right">
                    <strong>
                      {!Number.isNaN(grandTotalNormalStorage, 10) ? formatNumber(grandTotalNormalStorage.toFixed(0)) : '-'}
                    </strong>
                  </StyledFooterSubTableCell>
                  <StyledFooterSubTableCell align="right">
                    <strong>
                      {formatNumber(grandTotalDamStorage.toFixed(0))}
                    </strong>
                  </StyledFooterSubTableCell>
                  <StyledFooterSubTableCell />
                  <StyledFooterSubTableCell align="right">
                    <strong>
                      {formatNumber(grandTotalInflow.toFixed(2))}
                    </strong>
                  </StyledFooterSubTableCell>
                  <StyledFooterSubTableCell align="right">
                    <strong>
                      {formatNumber(grandTotalReleased.toFixed(2))}
                    </strong>
                  </StyledFooterSubTableCell>
                  <StyledFooterSubTableCell align="right">
                    <Tooltip title={t('กราฟรายปี')}>
                      <StyledIconButton onClick={handleChartSumByRegion(0)}>
                        <FontAwesomeIcon icon={faChartLine} style={{ color: '#888' }} />
                      </StyledIconButton>
                    </Tooltip>
                  </StyledFooterSubTableCell>
                </TableRow>
              )}
              {/* END grand total all regions */}

            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
}

DamLargeDataListsEgat.propTypes = {
  datas: PropTypes.instanceOf(Array).isRequired,
  handleModalChanage: PropTypes.func.isRequired,
  useRuleCurve: PropTypes.bool.isRequired,
};
